import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./BlogForm.css";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import { toast } from "react-toastify";
import { createBlog, getBlogById, listBlogImages, updateBlog, uploadBlogImage } from "../../../../api/blogs";
import { isUploadedBlogImagePath, resolveBlogImageSrc } from "../../../../utils/blogImages";
import { flushPendingPdfsInHtml } from "../../../../utils/flushPendingPdfsInHtml";
import { flushPendingImagesInHtml } from "../../../../utils/flushPendingImagesInHtml";
import CoverImageGalleryModal from "./CoverImageGalleryModal";
import {
    ArrowLeft,
    BookMarked,
    Check,
    FileEdit,
    Globe2,
    Images,
    Lock,
    PenLine,
    Rocket,
    Sparkles,
} from "lucide-react";

function slugify(value) {
    return String(value || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "");
}

const EMPTY_FORM = {
    title: "",
    slug: "",
    intro: "",
    image: "",
    author: "",
    status: "draft",
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
};

/** Local file chosen for cover â€” uploads on save only. */
const PENDING_COVER_IMAGE_KEY = "__pending_cover_upload__";
const MAX_BLOG_IMAGE_BYTES = 1024 * 1024;

function normalizeQuillHtml(html) {
    if (!html) return html;
    // Quill uses NBSP to preserve in-editor spacing; normalize for storage.
    return String(html).replace(/&nbsp;/g, " ").replace(/\u00A0/g, " ");
}

const BlogForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const pendingPdfFilesRef = useRef(new Map());
    const pendingImageFilesRef = useRef(new Map());
    const pendingCoverFileRef = useRef(null);

    const [form, setForm] = useState(EMPTY_FORM);

    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(isEdit);
    const [submitting, setSubmitting] = useState(false);
    const [pendingCoverPreviewUrl, setPendingCoverPreviewUrl] = useState("");
    const [pendingCoverName, setPendingCoverName] = useState("");
    const [coverGalleryOpen, setCoverGalleryOpen] = useState(false);
    const [serverImagePaths, setServerImagePaths] = useState([]);
    const [serverImagesLoading, setServerImagesLoading] = useState(false);
    const [serverImagesError, setServerImagesError] = useState("");
    const [coverError, setCoverError] = useState("");
    const [message, setMessage] = useState({ type: "", text: "" });

    const showFormError = useCallback((text) => {
        setMessage({ type: "error", text });
    }, []);

    const clearPendingCover = useCallback(() => {
        pendingCoverFileRef.current = null;
        setPendingCoverName("");
        setPendingCoverPreviewUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return "";
        });
    }, []);

    const loadServerImages = useCallback(async () => {
        setServerImagesLoading(true);
        setServerImagesError("");
        try {
            const data = await listBlogImages();
            const paths = (data.images || [])
                .map((item) => item.imageUrl)
                .filter(isUploadedBlogImagePath);
            setServerImagePaths(paths);
        } catch (err) {
            const errorMsg =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to load uploaded images.";
            setServerImagesError(errorMsg);
            setServerImagePaths([]);
        } finally {
            setServerImagesLoading(false);
        }
    }, []);

    useEffect(() => {
        if (coverGalleryOpen) loadServerImages();
    }, [coverGalleryOpen, loadServerImages]);

    const handlePickCoverFromGallery = (imagePath) => {
        clearPendingCover();
        setCoverError("");
        setForm((prev) => ({ ...prev, image: imagePath }));
        setCoverGalleryOpen(false);
        toast.success("Cover image selected from gallery.");
    };

    const selectedCoverPath =
        form.image === PENDING_COVER_IMAGE_KEY ? "" : isUploadedBlogImagePath(form.image) ? form.image : "";

    useEffect(() => {
        pendingPdfFilesRef.current.clear();
        pendingImageFilesRef.current.clear();
        clearPendingCover();
    }, [id, clearPendingCover]);

    // Fetch blog data if in edit mode
    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;

        const fetchBlog = async () => {
            setLoading(true);
            setMessage({ type: "", text: "" });
            try {
                const blog = await getBlogById(id);
                if (cancelled) return;
                clearPendingCover();
                const storedImage = isUploadedBlogImagePath(blog?.image) ? blog.image : "";
                setForm({
                    title: blog?.title || "",
                    slug: blog?.slug || "",
                    intro: blog?.intro || "",
                    image: storedImage,
                    author: blog?.author || "",
                    status: blog?.status === "draft" ? "draft" : "published",
                    metaTitle: blog?.metaTitle || "",
                    metaDescription: blog?.metaDescription || "",
                    metaKeywords: blog?.metaKeywords || "",
                });
                setContent(blog?.content || "");
            } catch (err) {
                if (cancelled) return;
                const errorMsg =
                    err?.response?.data?.message ||
                    err?.message ||
                    "Failed to load blog.";
                setMessage({ type: "error", text: errorMsg });
            } finally {
                if (cancelled) return;
                setLoading(false);
            }
        };
        fetchBlog();

        return () => {
            cancelled = true;
        };
    }, [id, isEdit, clearPendingCover]);

    // Ensure form is fully reset when switching from edit route to create route
    useEffect(() => {
        if (isEdit) return;
        clearPendingCover();
        setForm(EMPTY_FORM);
        setContent("");
        setMessage({ type: "", text: "" });
        setCoverError("");
        setLoading(false);
        setSubmitting(false);
    }, [isEdit, clearPendingCover]);

    const coverPreviewSrc = useMemo(() => {
        if (pendingCoverPreviewUrl) return pendingCoverPreviewUrl;
        return resolveBlogImageSrc(form.image);
    }, [pendingCoverPreviewUrl, form.image]);

    const hasValidCover = useMemo(() => {
        if (pendingCoverFileRef.current) {
            return true;
        }
        if (form.image === PENDING_COVER_IMAGE_KEY) {
            return Boolean(pendingCoverFileRef.current);
        }
        return isUploadedBlogImagePath(form.image);
    }, [form.image, pendingCoverName]);

    const coverUploadLabel =
        pendingCoverName || (hasValidCover ? "Replace cover image" : "Choose cover image");

    const canSubmit = useMemo(() => {
        if (submitting || loading) return false;
        if (!form.title.trim()) return false;
        if (!form.slug.trim()) return false;
        if (!form.intro.trim()) return false;
        if (!hasValidCover) return false;
        if (!content.trim()) return false;
        return true;
    }, [submitting, loading, form.title, form.slug, form.intro, hasValidCover, content]);

    const completion = useMemo(() => {
        const checks = [
            !!form.title.trim(),
            !!form.slug.trim(),
            !!form.intro.trim(),
            hasValidCover,
            !!form.author.trim(),
            !!content.trim()
        ];
        const done = checks.filter(Boolean).length;
        return Math.round((done / checks.length) * 100);
    }, [form.title, form.slug, form.intro, hasValidCover, form.author, content]);

    const primaryActionLabel = useMemo(() => {
        if (submitting) return "Saving…";
        if (form.status === "draft") return isEdit ? "Update draft" : "Save as draft";
        return isEdit ? "Update live post" : "Publish post";
    }, [submitting, form.status, isEdit]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleCoverFileSelect = (file) => {
        if (!file) return;

        const isAllowed = /^image\/(png|jpeg|jpg|gif|webp|svg\+xml)$/i.test(String(file.type || ""));
        if (!isAllowed) {
            setCoverError("Please choose a JPG, PNG, GIF, WEBP, or SVG image.");
            return;
        }
        if (file.size > MAX_BLOG_IMAGE_BYTES) {
            setCoverError("Image file is too large. Maximum size is 1 MB.");
            return;
        }

        setCoverError("");
        clearPendingCover();
        pendingCoverFileRef.current = file;
        setPendingCoverName(file.name);
        setPendingCoverPreviewUrl(URL.createObjectURL(file));
        setForm((prev) => ({ ...prev, image: PENDING_COVER_IMAGE_KEY }));
        setCoverGalleryOpen(false);
        toast.success("Cover selected — uploads when you save.");
    };

    const resolveCoverImageForSave = async () => {
        const pendingFile = pendingCoverFileRef.current;
        if (pendingFile) {
            let imageUrl;
            try {
                ({ imageUrl } = await uploadBlogImage(pendingFile));
            } catch (err) {
                throw new Error(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Failed to upload cover image. Please try again."
                );
            }
            if (!imageUrl || !isUploadedBlogImagePath(imageUrl)) {
                throw new Error("Cover image upload failed. Please choose another image and try again.");
            }
            return imageUrl;
        }

        if (!isUploadedBlogImagePath(form.image)) {
            throw new Error("Please choose a cover image.");
        }

        return form.image;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setMessage({ type: "", text: "" });
        setCoverError("");

        try {
            let contentToSave = normalizeQuillHtml(content);
            contentToSave = await flushPendingPdfsInHtml(contentToSave, pendingPdfFilesRef.current);
            contentToSave = await flushPendingImagesInHtml(contentToSave, pendingImageFilesRef.current);
            const imageToSave = await resolveCoverImageForSave();
            const payload = { ...form, image: imageToSave, content: contentToSave };

            if (isEdit) {
                await updateBlog(id, payload);
                setMessage({ type: "success", text: "Blog updated successfully!" });
                toast.success("Blog updated successfully!");
                clearPendingCover();
                navigate(`/dashboard/blog/${id}`);
            } else {
                await createBlog(payload);
                setMessage({ type: "success", text: "Blog created successfully!" });
                toast.success("Blog created successfully!");
                clearPendingCover();
                navigate("/dashboard/blog-list");
            }
        } catch (err) {
            console.error(err.response?.data || err);
            const errorText =
                err?.response?.data?.message ||
                err?.message ||
                (isEdit ? "Failed to update blog. Please try again." : "Failed to create blog. Please try again.");
            showFormError(errorText);
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <section className="abf addblog-container" aria-busy="true" aria-label="Loading editor">
                <div className="abf-skel abf-skel-topbar" />
                <div className="abf-skel abf-skel-header" />
                <div className="abf-skel-layout">
                    <div className="abf-skel abf-skel-card-lg" />
                    <div className="abf-skel abf-skel-card-sm" />
                </div>
            </section>
        );
    }

    return (
        <section className="abf addblog-container">
            <div className="abf-topbar">
                <Link className="abf-back-link" to="/dashboard/blog-list">
                    <ArrowLeft size={18} strokeWidth={2} aria-hidden />
                    Back to list
                </Link>
                <span
                    className={`abf-mode-pill ${isEdit ? "edit" : "create"} ${
                        form.status === "published" ? "published" : ""
                    }`}
                    title={form.status === "draft" ? "Draft â€” not on public blog" : "Published â€” visible on /blogs"}
                >
                    {form.status === "published" ? (
                        <Globe2 size={14} strokeWidth={2} className="abf-mode-pill-icon" aria-hidden />
                    ) : (
                        <FileEdit size={14} strokeWidth={2} className="abf-mode-pill-icon" aria-hidden />
                    )}
                    {form.status === "published" ? "Live" : "Draft"}
                </span>
            </div>

            <header className="addblog-header">
                <div className="abf-hero-row">
                    <div className="abf-hero-copy">
                        <p className="abf-page-eyebrow">
                            <PenLine size={15} strokeWidth={2} className="abf-page-eyebrow-icon" aria-hidden />
                            {isEdit ? "Update post" : "New post"}
                        </p>
                        <h1 className="addblog-title">
                            {isEdit ? "Refine your article" : "Write a new article"}
                        </h1>
                        <p className="addblog-subtitle">
                            {isEdit
                                ? "Polish metadata, swap the cover, and keep the body aligned with your brand."
                                : "Set title, slug, and excerpt, upload a cover image, then compose the full story."}
                        </p>
                    </div>
                    <div className="abf-progress-wrap" aria-label="Form completion">
                        <div className="abf-progress-head">
                            <span className="abf-progress-label">Completion</span>
                            <strong>{completion}%</strong>
                        </div>
                        <div className="abf-progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={completion}>
                            <span className="abf-progress-fill" style={{ width: `${completion}%` }} />
                        </div>
                    </div>
                </div>
            </header>

            {message.text && (
                <div className={`addblog-message ${message.type}`} role={message.type === "error" ? "alert" : "status"}>
                    {message.text}
                </div>
            )}

            <form className="addblog-form" onSubmit={handleSubmit}>
                <div className="abf-layout">
                    <div className="abf-main">
                        <div className="addblog-card addblog-card--basics">
                            <header className="abf-basics-head">
                                <div className="abf-basics-head-icon" aria-hidden>
                                    <BookMarked size={20} strokeWidth={2} />
                                </div>
                                <div className="abf-basics-head-copy">
                                    <p className="abf-basics-eyebrow">Post details</p>
                                    <h2 className="abf-basics-heading">Basics</h2>
                                    <p className="abf-basics-lede">
                                        Headline, URL slug, attribution, and the excerpt readers see before opening the full post.
                                    </p>
                                </div>
                            </header>

                            <div className="abf-basics-grid">
                                <div className="field abf-basics-field abf-basics-field--title">
                                    <label className="label" htmlFor="title">Title</label>
                                    <input
                                        id="title"
                                        type="text"
                                        name="title"
                                        placeholder="e.g. Why Anjouan is trending in iGaming"
                                        value={form.title}
                                        onChange={handleChange}
                                        required
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="field abf-basics-field abf-basics-field--author">
                                    <label className="label" htmlFor="author">Author</label>
                                    <input
                                        id="author"
                                        type="text"
                                        name="author"
                                        placeholder="e.g. Offshore Advisory"
                                        required
                                        value={form.author}
                                        onChange={handleChange}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="field abf-basics-field abf-basics-field--slug">
                                    <div className="label-row">
                                        <label className="label" htmlFor="slug">Slug</label>
                                        <button
                                            type="button"
                                            className="link-btn"
                                            onClick={() => setForm((p) => ({ ...p, slug: slugify(p.title) }))}
                                            disabled={!form.title.trim() || submitting}
                                            title="Generate slug from title"
                                        >
                                            Generate from title
                                        </button>
                                    </div>
                                    <input
                                        id="slug"
                                        type="text"
                                        name="slug"
                                        placeholder="e.g. anjouan-gaming-license"
                                        value={form.slug}
                                        onChange={handleChange}
                                        required
                                        autoComplete="off"
                                    />
                                    <div className="hint abf-basics-url-hint">
                                        <span className="abf-basics-url-prefix">Public URL:</span>
                                        <code className="abf-basics-url-sample">https://offshoreadvisory.pro/blog/{form.slug || "your-slug"}</code>
                                    </div>
                                </div>

                                <div className="field abf-basics-field abf-basics-field--intro">
                                    <label className="label" htmlFor="intro">Intro</label>
                                    <textarea
                                        id="intro"
                                        name="intro"
                                        placeholder="Short summary shown in the blog list and previewsâ€¦"
                                        value={form.intro}
                                        onChange={handleChange}
                                        rows={4}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="addblog-card addblog-card--seo">
                            <header className="abf-basics-head">
                                <div className="abf-basics-head-icon abf-basics-head-icon--seo" aria-hidden>
                                    <Globe2 size={20} strokeWidth={2} />
                                </div>
                                <div className="abf-basics-head-copy">
                                    <p className="abf-basics-eyebrow">Search & sharing</p>
                                    <h2 className="abf-basics-heading">SEO metadata</h2>
                                    <p className="abf-basics-lede">
                                        Optional meta title, description, and keywords for search engines and social previews.
                                        Leave blank to use the post title and intro.
                                    </p>
                                </div>
                            </header>

                            <div className="abf-basics-grid abf-seo-grid">
                                <div className="field abf-basics-field abf-basics-field--meta-title">
                                    <label className="label" htmlFor="metaTitle">Meta title</label>
                                    <input
                                        id="metaTitle"
                                        type="text"
                                        name="metaTitle"
                                        placeholder={form.title || "Defaults to post title"}
                                        value={form.metaTitle}
                                        onChange={handleChange}
                                        autoComplete="off"
                                    />
                                </div>

                                <div className="field abf-basics-field abf-basics-field--meta-keywords">
                                    <label className="label" htmlFor="metaKeywords">Keywords</label>
                                    <input
                                        id="metaKeywords"
                                        type="text"
                                        name="metaKeywords"
                                        placeholder="e.g. offshore license, igaming, company formation"
                                        value={form.metaKeywords}
                                        onChange={handleChange}
                                        autoComplete="off"
                                    />
                                    <div className="hint">Comma-separated keywords for search engines.</div>
                                </div>

                                <div className="field abf-basics-field abf-basics-field--meta-description">
                                    <label className="label" htmlFor="metaDescription">Meta description</label>
                                    <textarea
                                        id="metaDescription"
                                        name="metaDescription"
                                        placeholder={form.intro || "Defaults to post intro"}
                                        value={form.metaDescription}
                                        onChange={handleChange}
                                        rows={3}
                                    />
                                </div>
                            </div>
                        </div>

                      
                    </div>

                    <aside className="abf-side" aria-label="Publishing panel">
                        <div className="abf-side-stack abf-sticky-card">
                        <div className="addblog-card addblog-image-preview">
                            <header className="abf-cover-card-head">
                                <div className="abf-cover-card-head-icon" aria-hidden>
                                    <Images size={20} strokeWidth={2} />
                                </div>
                                <div className="abf-cover-card-head-copy">
                                    <p className="abf-cover-card-eyebrow">Visual</p>
                                    <h3 className="abf-cover-card-title">Cover image</h3>
                                </div>
                            </header>
                            <div className="abf-cover-card-body">
                                <div className="field addblog-image-field">
                                    <button
                                        type="button"
                                        className="addblog-cover-open-btn"
                                        onClick={() => {
                                            setCoverError("");
                                            setCoverGalleryOpen(true);
                                        }}
                                        disabled={submitting}
                                    >
                                        <Images size={18} strokeWidth={2} className="addblog-image-icon" aria-hidden />
                                        {hasValidCover ? "Change cover image" : "Select cover image"}
                                    </button>

                                    <div className="hint">
                                        Opens the gallery to pick an existing upload or choose a new file from your computer. Max size: 1 MB.
                                    </div>
                                    {coverError ? (
                                        <p className="abf-cover-error" role="alert">
                                            {coverError}
                                        </p>
                                    ) : null}
                                </div>

                                <div className="addblog-image-preview-box">
                                    {hasValidCover && coverPreviewSrc ? (
                                        <img
                                            className="addblog-image-preview-img"
                                            src={coverPreviewSrc}
                                            alt="Cover preview"
                                        />
                                    ) : (
                                        <p className="abf-cover-empty-hint">No cover selected yet.</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="addblog-card addblog-publish-card">
                            <header className="abf-publish-head">
                                <div className="abf-publish-head-icon">
                                    <Sparkles size={20} strokeWidth={2} aria-hidden />
                                </div>
                                <div className="abf-publish-head-copy">
                                    <p className="abf-publish-eyebrow">Visibility</p>
                                    <h3 className="abf-publish-heading">Publication</h3>
                                </div>
                            </header>

                            <p className="abf-publish-lede">
                                Control where readers find this story. Live posts use{" "}
                                <code className="abf-inline-code">/blogs/your-slug</code>.
                            </p>

                            <div className="abf-publish-summary" aria-live="polite">
                                <span
                                    className={`abf-publish-pulse ${form.status === "published" ? "is-live" : ""}`}
                                    aria-hidden
                                />
                                <span className="abf-publish-summary-text">
                                    {form.status === "published"
                                        ? "Visible on the public blog"
                                        : "Visible only in this dashboard"}
                                </span>
                            </div>

                            <div className="abf-publish-grid" role="group" aria-label="Post visibility">
                                <button
                                    type="button"
                                    className={`abf-publish-tile ${form.status === "draft" ? "is-selected" : ""}`}
                                    onClick={() => setForm((p) => ({ ...p, status: "draft" }))}
                                    aria-pressed={form.status === "draft"}
                                >
                                    <span className="abf-publish-tile-glow" aria-hidden />
                                    {form.status === "draft" ? (
                                        <span className="abf-publish-tile-check" aria-hidden>
                                            <Check size={14} strokeWidth={3} />
                                        </span>
                                    ) : null}
                                    <span className="abf-publish-tile-icon abf-publish-tile-icon--draft">
                                        <FileEdit size={22} strokeWidth={2} aria-hidden />
                                    </span>
                                    <span className="abf-publish-tile-title">Draft</span>
                                    <span className="abf-publish-tile-desc">
                                        Keep editing privately. Not shown on the marketing site.
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    className={`abf-publish-tile ${form.status === "published" ? "is-selected" : ""}`}
                                    onClick={() => setForm((p) => ({ ...p, status: "published" }))}
                                    aria-pressed={form.status === "published"}
                                >
                                    <span className="abf-publish-tile-glow abf-publish-tile-glow--live" aria-hidden />
                                    {form.status === "published" ? (
                                        <span className="abf-publish-tile-check abf-publish-tile-check--live" aria-hidden>
                                            <Check size={14} strokeWidth={3} />
                                        </span>
                                    ) : null}
                                    <span className="abf-publish-tile-icon abf-publish-tile-icon--live">
                                        <Globe2 size={22} strokeWidth={2} aria-hidden />
                                    </span>
                                    <span className="abf-publish-tile-title">Published</span>
                                    <span className="abf-publish-tile-desc">
                                        Appears in blog listings and at its public URL.
                                    </span>
                                </button>
                            </div>

                            <div className={`abf-publish-callout ${form.status === "published" ? "is-live" : "is-draft"}`}>
                                {form.status === "draft" ? (
                                    <Lock size={16} strokeWidth={2} className="abf-publish-callout-icon" aria-hidden />
                                ) : (
                                    <Rocket size={16} strokeWidth={2} className="abf-publish-callout-icon" aria-hidden />
                                )}
                                <p className="abf-publish-callout-text">
                                    {form.status === "draft"
                                        ? "Drafts stay in your admin library until you switch to Published and save."
                                        : "Readers can open this post from /blogs â€” ensure slug and content are final."}
                                </p>
                            </div>
                        </div>
                        </div>

                    </aside>
                </div>


                <RichTextEditor
                            initialContent={content}
                            onChange={(html) => setContent(html)}
                            pendingPdfFilesRef={pendingPdfFilesRef}
                            pendingImageFilesRef={pendingImageFilesRef}
                        />
                <div className="addblog-submit-container">
                    <Link to="/dashboard/blog-list" className="abf-cancel-link">Cancel</Link>
                    <button type="submit" disabled={!canSubmit}>
                        {primaryActionLabel}
                    </button>
                </div>
            </form>

            <CoverImageGalleryModal
                open={coverGalleryOpen}
                imagePaths={serverImagePaths}
                selectedPath={selectedCoverPath}
                loading={serverImagesLoading}
                error={serverImagesError}
                uploadError={coverError}
                uploadLabel={coverUploadLabel}
                uploadDisabled={submitting}
                onClose={() => setCoverGalleryOpen(false)}
                onPick={handlePickCoverFromGallery}
                onRetry={loadServerImages}
                onCoverFileSelect={handleCoverFileSelect}
            />
        </section>
    );
};

export default BlogForm;
